// Palette + type tokens for the first-party "Rate your experience" page
// (patient portal, /me/rate) and the EMR ratings screens that reuse StarTiles.
//
// The look is modelled on Trustpilot's evaluate flow. STAR_TIER_COLORS are
// Trustpilot's published values; every other colour was pixel-sampled from the
// reference screenshots (~/Desktop/Trustpilot Screenshots).
//
// ⚠️ Those macOS screenshots are Display-P3 encoded (cICP 12,13 + an iCCP
// chunk, no sRGB chunk), so a raw pixel read is a P3 value, NOT the CSS hex
// Trustpilot ships. Convert P3 → sRGB before using a sample as a token
// (sRGB transfer curve on both sides around the linear matrix
// [1.2249401,-0.2249404,0; -0.0420569,1.0420571,0; -0.0196376,-0.0786361,1.0982735]).
// Neutrals barely move (Δ ≤ 1/255); saturated blues/greens move by 5–20/255 —
// e.g. the sampled accent #4256B6 is really #3C57BC. Sanity check: the sampled
// star tiles convert exactly to the published tier colours below.
//
// Keep this file the single source of truth for the page's colors — the
// components never hard-code hex values.

export const RATING_FONT_FAMILY =
  '"Helvetica Neue", Helvetica, Arial, sans-serif';

// Trustpilot's mobile breakpoint. Plain media queries (no theme access) so the
// shared components work without a MUI ThemeProvider, e.g. in Jest.
export const RATING_MOBILE_MEDIA_QUERY = "@media (max-width: 599.95px)";

// Column content widths (excluding the 16px side padding).
export const RATING_COLUMN = 480;
export const RATING_THANKS_COLUMN = 584;

// Star tile sizes: 46px while the patient is picking a rating, 40px once the
// form is open (Trustpilot shrinks them on the first click).
export const RATING_TILE_PICK = 46;
export const RATING_TILE_FORM = 40;

// Visually hidden but still in the accessibility tree / focusable (never
// display:none). Shared by the star radios, the honeypot and SR-only copy.
export const VISUALLY_HIDDEN = {
  position: "absolute",
  width: "1px",
  height: "1px",
  margin: "-1px",
  padding: 0,
  border: 0,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  whiteSpace: "nowrap",
};

export const RATING_COLORS = {
  headerBar: "#1B1A1B",

  pageBg: "#FFFFFF",
  thanksBg: "#F9F8F5",
  thanksLowerBand: "#F3F0ED",

  companyCard: "#F9F8F5",
  chipsPanel: "#F9F7F3",
  noticeBox: "#FCFBF4",
  infoBanner: "#C8F1F5",

  textPrimary: "#2E2F2A",
  textSecondary: "#605E57",
  // Pure black is only used for the thank-you h1 and the band card title.
  headingInk: "#000000",
  placeholder: "#757575",

  borderInput: "#C5C0BC",
  // The comment textarea has a darker border than the other inputs.
  borderTextarea: "#605E57",
  borderCard: "#DBD6D2",
  borderChip: "#E1E0DC",
  chipFill: "#E7E7E6",
  thumbBorder: "#E1E1DE",
  connector: "#E5E5DE",

  accent: "#3C57BC",
  accentHover: "#354CA6",
  accentSoftBg: "#7A9DFA",
  accentSoftText: "#111011",
  // Checked checkbox fill and the pale pill button.
  accentPaleBg: "#E5EBFE",

  // The "no incentives" shield badge.
  badgeFill: "#7AA0EA",
  badgeRim: "#4E81E5",
  badgeGlyph: "#1C1C1C",

  starEmpty: "#DCDCE6",
  starGlyph: "#FFFFFF",

  celebrateStar: "#0BDA8E",
  celebrateCard: "#B2F8CC",
  celebrateDeco: "#CAFCD8",
  celebrateInk: "#111511",

  // Not a Trustpilot token — the page needs one error color for inline
  // submit failures and there is nothing suitable in the sampled palette.
  error: "#B3261E",
};

// Star tiers 1..5. When n stars are selected (or hovered) tiles 1..n ALL take
// tier n's color — a 2-star rating is two orange tiles, never red + orange.
export const STAR_TIER_COLORS = {
  1: "#FF3722",
  2: "#FF8622",
  3: "#FFCE00",
  4: "#73CF11",
  5: "#00B67A",
};

export function starTierColor(stars) {
  return STAR_TIER_COLORS[stars] || RATING_COLORS.starEmpty;
}
